import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './TeamMeetings.css';

const API_BASE = process.env.REACT_APP_API_BASE;

// Hilfsfunktion für deutsches Format (TT.MM.JJJJ)
const formatDateDE = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const TeamMeetings = () => {
    const [meetingGroups, setMeetingGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newBoxDate, setNewBoxDate] = useState('');
    const [newBoxTime, setNewBoxTime] = useState('');
    const [quickTopics, setQuickTopics] = useState({});
    const [reactionData, setReactionData] = useState({});
    const [expandedMeeting, setExpandedMeeting] = useState(null);

    const fetchMeetings = useCallback(async () => {
        try {
            const token = localStorage.getItem('discord_token');
            const res = await axios.get(`${API_BASE}/api/team-meetings`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const grouped = {};
            res.data.forEach(m => {
                const dateKey = m.date.split('T')[0];
                if (!grouped[dateKey]) grouped[dateKey] = [];
                grouped[dateKey].push(m);
            });
            
            const groups = Object.keys(grouped).sort((a, b) => new Date(b) - new Date(a)).map(date => ({
                date,
                fullDate: grouped[date][0].date,
                discordMessageId: grouped[date][0].discord_message_id,
                points: grouped[date]
            }));
            setMeetingGroups(groups);
        } catch (err) { 
            console.error(err); 
        } finally { 
            setLoading(false); 
        }
    }, []);

    const fetchReactions = useCallback(async (messageId) => {
        if (!messageId) return;
        try {
            const token = localStorage.getItem('discord_token');
            const res = await axios.get(`${API_BASE}/api/team-meetings/reactions/${messageId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReactionData(prev => ({
                ...prev,
                [messageId]: res.data
            }));
        } catch (err) {
            console.error('Error fetching reactions:', err);
        }
    }, []);

    useEffect(() => {
        fetchMeetings();
    }, [fetchMeetings]);

    const handleAddPointToDate = async (e, fullDate) => {
        e.preventDefault();
        const topic = quickTopics[fullDate];
        if (!topic || !topic.trim()) return;

        try {
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/team-meetings`, {
                topic: topic.trim(),
                date: fullDate 
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setQuickTopics({ ...quickTopics, [fullDate]: '' });
            fetchMeetings();
        } catch (err) {
            console.error('Error adding point:', err);
            alert("Fehler beim Hinzufügen!");
        }
    };

    const handleCreateBox = async (e) => {
        e.preventDefault();
        if (!newBoxDate || !newBoxTime) return;
        const datetime = `${newBoxDate}T${newBoxTime}:00`;
        try {
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/team-meetings/add-box`, { date: datetime }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNewBoxDate('');
            setNewBoxTime('');
            fetchMeetings();
        } catch (err) {
            alert("Fehler beim Erstellen!");
        }
    };


    const handleDelete = async (id) => {
        if (!window.confirm('Punkt löschen?')) return;
        try {
            const token = localStorage.getItem('discord_token');
            await axios.delete(`${API_BASE}/api/team-meetings/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMeetings();
        } catch (err) {
            alert("Fehler beim Löschen!");
        }
    };

    const handleStatusChange = async (id, newStatus, topic, date) => {
        try {
            const token = localStorage.getItem('discord_token');
            await axios.put(`${API_BASE}/api/team-meetings/${id}`, { status: newStatus, topic, date }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMeetings();
        } catch (err) {
            alert("Fehler beim Ändern des Status!");
        }
    };

    const handleArchiveAll = async (fullDate) => {
        if (!window.confirm('Alle Punkte für dieses Datum archivieren?')) return;
        try {
            const token = localStorage.getItem('discord_token');
            await axios.put(`${API_BASE}/api/team-meetings/archive-all/${fullDate}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMeetings();
        } catch (err) {
            alert("Fehler beim Archivieren!");
        }
    };

    const handleDeleteBox = async (fullDate) => {
        if (!window.confirm('Die gesamte Box für dieses Datum löschen? Alle Punkte gehen verloren!')) return;
        try {
            const token = localStorage.getItem('discord_token');
            await axios.delete(`${API_BASE}/api/team-meetings/delete-box/${fullDate}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMeetings();
        } catch (err) {
            alert("Fehler beim Löschen der Box!");
        }
    };

    const handleToggleReactions = async (messageId) => {
        if (expandedMeeting === messageId) {
            setExpandedMeeting(null);
        } else {
            setExpandedMeeting(messageId);
            if (!reactionData[messageId]) {
                await fetchReactions(messageId);
            }
        }
    };

    return (
        <div className="team-meetings-container">
            <div className="dashboard-main">
                <div className="team-meetings-header">
                    <h1 className="team-meetings-title">🗣️ Team Besprechung</h1>
                    <p className="team-meetings-subtitle">Verwalte Themen für anstehende Termine</p>
                </div>

                {/* Box erstellen */}
                {meetingGroups.length === 0 && (
                    <div className="meeting-create-card">
                        <form onSubmit={handleCreateBox}>
                            <div className="meeting-create-row">
                                <div className="form-group">
                                    <label className="form-label">Datum</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={newBoxDate}
                                        onChange={(e) => setNewBoxDate(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Uhrzeit</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newBoxTime}
                                        onChange={(e) => setNewBoxTime(e.target.value)}
                                        required
                                    />
                                </div>
                                <button type="submit" className="btn btn-meeting btn-meeting-success">
                                    📦 Neue Teambesprechungen erstellen
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Meetings Liste */}
                <div className="meeting-list">
                    {loading ? (
                        <div className="meeting-loading">
                            <div className="spinner"></div>
                            <p className="team-meetings-subtitle">Lädt...</p>
                        </div>
                    ) : (
                        meetingGroups.map(group => (
                            <div key={group.date} className="meeting-item-card">
                                <div className="meeting-item-header">
                                    <h5 className="meeting-item-title mb-0">
                                        📅 {formatDateDE(group.date)}
                                        {group.fullDate.includes('T') 
                                            ? ` um ${group.fullDate.split('T')[1].slice(0,5)} Uhr` 
                                            : ''}
                                    </h5>
                                    <div className="meeting-item-actions">
                                        {group.discordMessageId && (
                                            <button 
                                                className={`btn-action-small ${expandedMeeting === group.discordMessageId ? 'primary' : 'info'}`} 
                                                onClick={() => handleToggleReactions(group.discordMessageId)}
                                            >
                                                👥 Teilnahme
                                            </button>
                                        )}
                                        <button className="btn-action-small warning" onClick={() => handleArchiveAll(group.fullDate)}>
                                            📁 Archivieren
                                        </button>
                                        <button className="btn-action-small danger" onClick={() => handleDeleteBox(group.fullDate)}>
                                            🗑️ Box löschen
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Reactions Display */}
                                {expandedMeeting === group.discordMessageId && reactionData[group.discordMessageId] && (
                                    <div className="meeting-reactions-container">
                                        <div className="meeting-reactions-summary">
                                            <span className="reaction-count yes">✅ {reactionData[group.discordMessageId].yesCount}</span>
                                            <span className="reaction-count no">❌ {reactionData[group.discordMessageId].noCount}</span>
                                            <span className="reaction-count pending">⏳ {reactionData[group.discordMessageId].notReactedCount} nicht reagiert</span>
                                        </div>
                                        
                                        <div className="meeting-reactions-grid">
                                            <div className="reaction-column">
                                                <h6>✅ Nehmen teil</h6>
                                                {reactionData[group.discordMessageId].yes.length > 0 ? (
                                                    <ul className="reaction-list">
                                                        {reactionData[group.discordMessageId].yes.map(user => (
                                                            <li key={user.id} className="reaction-user">
                                                                <img src={user.avatarURL || '/default-avatar.png'} alt="" className="reaction-avatar" />
                                                                <span>{user.displayName || user.username}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="no-data">Noch keine Zusagen</p>
                                                )}
                                            </div>
                                            
                                            <div className="reaction-column">
                                                <h6>❌ Können nicht</h6>
                                                {reactionData[group.discordMessageId].no.length > 0 ? (
                                                    <ul className="reaction-list">
                                                        {reactionData[group.discordMessageId].no.map(user => (
                                                            <li key={user.id} className="reaction-user">
                                                                <img src={user.avatarURL || '/default-avatar.png'} alt="" className="reaction-avatar" />
                                                                <span>{user.displayName || user.username}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="no-data">Keine Absagen</p>
                                                )}
                                            </div>
                                            
                                            <div className="reaction-column not-reacted">
                                                <h6>⏳ Nicht reagiert</h6>
                                                {reactionData[group.discordMessageId].notReacted.length > 0 ? (
                                                    <ul className="reaction-list">
                                                        {reactionData[group.discordMessageId].notReacted.map(user => (
                                                            <li key={user.id} className="reaction-user pending">
                                                                <img src={user.avatarURL || '/default-avatar.png'} alt="" className="reaction-avatar" />
                                                                <span>{user.displayName || user.username}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="no-data">Alle haben reagiert!</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="meeting-item-body">
                                    <form onSubmit={(e) => handleAddPointToDate(e, group.fullDate)} className="mb-3">
                                        <div className="meeting-input-group">
                                            <textarea
                                                className="form-control"
                                                placeholder="Neuen Besprechungspunkt hinzufügen..."
                                                value={quickTopics[group.fullDate] || ''}
                                                onChange={(e) => setQuickTopics({...quickTopics, [group.fullDate]: e.target.value})}
                                                rows="2"
                                            />
                                            <button className="btn btn-meeting btn-meeting-primary" type="submit">➕ Hinzufügen</button>
                                        </div>
                                    </form>

                                    <div className="meeting-points-container">
                                        {group.points
                                            .filter(p => p.topic && p.topic.trim() !== '')
                                            .map(point => (
                                            <div key={point.id} className={`meeting-point-item ${point.status === 'besprochen' ? 'besprochen-bg' : ''}`}>
                                                <span className="meeting-point-text">{point.topic}</span>
                                                <select className={`meeting-point-badge ${point.status}`} value={point.status} onChange={(e) => handleStatusChange(point.id, e.target.value, point.topic, point.date)}>
                                                    <option value="offen">Offen</option>
                                                    <option value="besprochen">Besprochen</option>
                                                    <option value="archiviert">Archiviert</option>
                                                </select>
                                                <div className="meeting-point-actions">
                                                    <button className="btn-action-small danger" onClick={() => handleDelete(point.id)}>X</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {group.points.filter(p => p.topic && p.topic.trim() !== '').length === 0 && (
                                        <p className="meeting-empty-text">Noch keine Themen zum Besprechen eingetragen.</p>
                                    )}
                                </div>        
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamMeetings;
